"use client"

import { useRef, useState, useEffect, useCallback } from "react"
import { motion } from "framer-motion"
import styles from "../../styles/AboutUs.module.css" // Assuming this styles object is in use

// Assuming these paths are correct in your actual project structure
import eastAfricaComAward from "../../assets/award3.png"
import eastAfricaComAwards from "../../assets/award1.png"
import easternAfricaStartupAwards from "../../assets/award2.png"
import award from "../../assets/award4.png"
import pcidssAwards from "../../assets/pcidssAward.png"

// Define a breakpoint for mobile/desktop logic
const MOBILE_BREAKPOINT = 768 // For screens smaller than 768px, treat it as mobile

const AwardsSection = () => {
  const awardSliderRef = useRef(null)
  const [awardCurrentPage, setAwardCurrentPage] = useState(1)
  const [awardItemsPerPage, setAwardItemsPerPage] = useState(3)
  const [isMobile, setIsMobile] = useState(false) // New state for mobile view detection
  const [awardTouchStart, setAwardTouchStart] = useState(0)
  const [awardTouchEnd, setAwardTouchEnd] = useState(0)
  const [hoveredCard, setHoveredCard] = useState(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(true)

const awardItems = [
    { id: 1, image: pcidssAwards, title: "PCI DSS certified" },
    { id: 2, image: eastAfricaComAwards, title: "Fintech Innovation of the Year 2023" },
    { id: 3, image: easternAfricaStartupAwards, title: "Winner - Ethiopia Visa Everywhere Initiative 2020" },
    { id: 4, image: eastAfricaComAward, title: "Stride Ethiopia Award 2024" },
    { id: 5, image: award, title: "Regional Finalist - Eastern Africa Startup Awards 2024" },
  ];

  const handleAwardPageChange = useCallback((pageNumber) => {
    if (awardSliderRef.current && awardItemsPerPage > 0) {
      const newPage = pageNumber
      const totalPages = Math.ceil(awardItems.length / awardItemsPerPage)
      const containerWidth = awardSliderRef.current.offsetWidth
      
      let scrollDistance = 0;
      if (isMobile) {
          const cardElement = awardSliderRef.current.children[0];
          if (cardElement) {
              const cardWidthWithGap = cardElement.offsetWidth + 30; 
              scrollDistance = (newPage - 1) * cardWidthWithGap;
          } else {
              scrollDistance = (newPage - 1) * containerWidth; 
          }
      } else {
          scrollDistance = (newPage - 1) * containerWidth 
      }

      awardSliderRef.current.scrollLeft = scrollDistance
      setAwardCurrentPage(newPage)
      
      setShowLeftArrow(newPage > 1)
      setShowRightArrow(newPage < totalPages)
    }
  }, [awardItemsPerPage, awardItems.length, isMobile]) 

  const handleNext = () => {
    if (awardCurrentPage < Math.ceil(awardItems.length / awardItemsPerPage)) {
      handleAwardPageChange(awardCurrentPage + 1)
    }
  }

  const handlePrev = () => {
    if (awardCurrentPage > 1) {
      handleAwardPageChange(awardCurrentPage - 1)
    }
  }

  const handleAwardTouchStart = (e) => {
    setAwardTouchStart(e.targetTouches[0].clientX)
  }

  const handleAwardTouchMove = (e) => {
    setAwardTouchEnd(e.targetTouches[0].clientX)
  }

  const handleAwardTouchEnd = () => {
    const totalPages = Math.ceil(awardItems.length / awardItemsPerPage)

    if (awardTouchStart - awardTouchEnd > 50) {
      if (awardCurrentPage < totalPages) {
        handleAwardPageChange(awardCurrentPage + 1)
      }
    }
    if (awardTouchStart - awardTouchEnd < -50) {
      if (awardCurrentPage > 1) {
        handleAwardPageChange(awardCurrentPage - 1)
      }
    }
  }

  useEffect(() => {
    const handleResize = () => {
      const mobileView = window.innerWidth < MOBILE_BREAKPOINT
      setIsMobile(mobileView)
      
      const newItemsPerPage = mobileView ? 1 : 3
      
      if (newItemsPerPage !== awardItemsPerPage) {
        setAwardItemsPerPage(newItemsPerPage)
        setAwardCurrentPage(1) 
      } else {
         const totalPages = Math.ceil(awardItems.length / newItemsPerPage)
         setShowLeftArrow(awardCurrentPage > 1)
         setShowRightArrow(awardCurrentPage < totalPages)
      }
    }

    handleResize()

    window.addEventListener("resize", handleResize)

    return () => window.removeEventListener("resize", handleResize)
  }, [awardItemsPerPage, awardItems.length, awardCurrentPage]) 

  const containerVariants = {
    hidden: { 
      opacity: 0,
      y: 20 
    }, 
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        staggerChildren: 0.2, 
        delayChildren: 0.3, 
      },
    },
  }

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      x: -100 
    },
    visible: {
      opacity: 1,
      x: 0, 
      transition: {
        duration: 0.7, 
        ease: "easeOut",
      },
    },
  }

  const imageHoverVariants = {
    normal: {
      scale: 1,
      y: 0,
    },
    hover: {
      scale: 2.00,
      y: -10,
    }
  }

  const cardHoverVariants = {
    normal: {
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
      y: 0,
    },
    hover: {
      boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
      y: -5,
    }
  }

  const arrowVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 }
    },
    hover: { 
      scale: 1.1,
      transition: { duration: 0.2 }
    }
  }

  // Calculate total pages for dot indicators
  const totalPages = Math.ceil(awardItems.length / awardItemsPerPage)

  return (
    <>
      <motion.div
        className={styles.award}
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.6 }}
      >
        <h2
          style={{
            fontSize: "32px",
            color: "#333",
            marginTop: "50px",
            marginBottom: "50px",
            lineHeight: 1.2,
            textAlign: "left",
            flex: 1,
            marginLeft: isMobile ? "20px" : "150px", 
          }}
        >
          Innovation and Recognition
        </h2>
        <p
          style={{
            fontSize: "16px",
            color: "black",
            lineHeight: 1.7,
            textAlign: "left",
            flex: 1,
            marginLeft: isMobile ? "20px" : "150px",
            marginRight: isMobile ? "20px" : "auto", 
          }}
        >
          Arifpay remains committed to innovation, continuously exploring new technologies and trends to enhance its
          payment solutions and deliver exceptional customer experiences.
        </p>
      </motion.div>
      
      <div
        className={styles.awardSection}
        style={{
          overflowX: "hidden",
          padding: isMobile ? "0 15px" : "0 20px", 
          position: "relative",
          maxWidth: "1500px",
          margin: "0 auto",
        }}
      >
        {/* Mobile Arrows */}
        {showLeftArrow && isMobile && ( 
          <motion.button
            style={{
              position: "absolute",
              left: "40px",
              overflow:"hidden",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 20,
              background: "white", 
              border: "1px solid #ddd", 
              borderRadius: "50%",
               width: "50px",
              height: "50px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
              color: "rgba(86, 74, 195, 0.9)",
              fontSize: "24px",
              fontWeight: "bold",
            }}
            variants={arrowVariants}
            initial="hidden"
            animate="visible"
            whileHover="hover"
            onClick={handlePrev}
            aria-label="Previous awards"
          >
            ‹
          </motion.button>
        )}

        {showRightArrow && isMobile && ( 
          <motion.button
            style={{
              position: "absolute",
              overflow:'hidden',
              right: "50px", 
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 20,
              background: "white", 
              border: "1px solid #ddd",
              borderRadius: "50%",
               width: "50px",
              height: "50px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
              color: "rgba(86, 74, 195, 0.9)",
              fontSize: "24px",
              fontWeight: "bold",
            }}
            variants={arrowVariants}
            initial="hidden"
            animate="visible"
            whileHover="hover"
            onClick={handleNext}
            aria-label="Next awards"
          >
            ›
          </motion.button>
        )}

        {/* Desktop Arrows - FIXED POSITIONING */}
        {showLeftArrow && !isMobile && ( 
          <motion.button
            style={{
              position: "absolute",
              left: "20px",
              overflow:'hidden',
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 20,
              background: "white",
              border: "2px solid rgba(86, 74, 195, 0.9)",
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.3)",
              color: "rgba(86, 74, 195, 0.9)",
              fontSize: "28px",
              fontWeight: "bold",
              transition: "all 0.3s ease",
            }}
            variants={arrowVariants}
            initial="hidden"
            animate="visible"
            whileHover={{ 
              scale: 1.1,
              backgroundColor: "rgba(86, 74, 195, 0.1)"
            }}
            onClick={handlePrev}
            aria-label="Previous awards"
          >
            ‹
          </motion.button>
        )}

        {showRightArrow && !isMobile && (
          <motion.button
            style={{
              position: "absolute",
              right: "20px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 20,
              background: "white",
              border: "2px solid rgba(86, 74, 195, 0.9)",
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.3)",
              color: "rgba(86, 74, 195, 0.9)",
              fontSize: "28px",
              fontWeight: "bold",
              transition: "all 0.3s ease",
            }}
            variants={arrowVariants}
            initial="hidden"
            animate="visible"
            whileHover={{ 
              scale: 1.1,
              backgroundColor: "rgba(86, 74, 195, 0.1)"
            }}
            onClick={handleNext}
            aria-label="Next awards"
          >
            ›
          </motion.button>
        )}

        <motion.div
          className={styles.awardSlider}
          ref={awardSliderRef}
          style={{
            overflowX: "hidden",
            scrollBehavior: "smooth",
            display: "flex",
            gap: "30px",
            padding: isMobile ? "0 10px" : "0 20px", // Added padding to ensure arrows don't overlap content
          }}
          onTouchStart={handleAwardTouchStart}
          onTouchMove={handleAwardTouchMove}
          onTouchEnd={handleAwardTouchEnd}
          
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {awardItems.map((award) => (
            <motion.div
              key={award.id}
              className={styles.awardCard}
              style={{
                overflow: "hidden",
                height: "400px",
                minHeight: "400px",
                width: isMobile ? `calc(100% - 20px)` : "380px", // Adjusted width
                minWidth: isMobile ? `calc(100% - 20px)` : "380px", // Adjusted minWidth
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                position: "relative",
                cursor: "pointer",
                borderRadius: "16px",
                backgroundColor: "white",
                flex: "0 0 auto",
                margin: isMobile ? "0 5px" : "0", // Added margin for better spacing
              }}
              variants={cardVariants}
              whileHover="hover" 
              onHoverStart={() => setHoveredCard(award.id)}
              onHoverEnd={() => setHoveredCard(null)}
            >
              <motion.div 
                style={{ 
                  flex: "1", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  position: "relative",
                  zIndex: 2,
                  minHeight: "250px",
                  padding: "30px",
                }}
              >
                <motion.img
                  style={{
                    width: "100%",
                    maxWidth: "220px",
                    height: "auto",
                    maxHeight: "150px",
                    margin: "0 auto",
                    display: "block",
                    objectFit: "contain",
                    objectPosition: "center",
                    filter: hoveredCard === award.id ? "drop-shadow(0 15px 20px rgba(0,0,0,0.2))" : "drop-shadow(0 4px 8px rgba(0,0,0,0.1))",
                  }}
                  src={award.image || "/placeholder.svg"}
                  alt={award.title}
                  variants={imageHoverVariants}
                  whileHover="hover"
                />
                {hoveredCard === award.id && (
                  <motion.div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "100%",
                      height: "100%",
                      background: "radial-gradient(circle, rgba(139, 195, 74, 0.1) 0%, rgba(139, 195, 74, 0) 70%)",
                      borderRadius: "50%",
                      zIndex: 1,
                    }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </motion.div>
              <div 
                style={{ 
                  padding: "0 30px 30px 30px",
                  textAlign: "center",
                  minHeight: "150px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  position: "relative",
                  zIndex: 2,
                  backgroundColor: "white",
                  borderRadius: "0 0 16px 16px",
                }}
              >
                {award.title && (
                  <h4 
                    style={{ 
                      fontSize: "18px",
                      fontWeight: "bold", 
                      margin: "0 0 12px 0",
                      color: "#333",
                      lineHeight: "1.3"
                    }}
                  >
                    {award.title}
                  </h4>
                )}
                <p 
                  className={styles.awardDescription}
                  style={{
                    margin: "0",
                    fontSize: "15px",
                    lineHeight: "1.5",
                    color: "#666",
                    display: "-webkit-box",
                    WebkitLineClamp: "3",
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    minHeight: "70px",
                  }}
                >
                  {award.description}
                </p>
              </div>
              
              <motion.div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: "16px",
                  boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)",
                  zIndex: 1,
                  opacity: 0,
                }}
                animate={{ 
                  opacity: hoveredCard === award.id ? 1 : 0 
                }}
                transition={{ duration: 0.3 }}
              />
            </motion.div>
          ))}
        </motion.div>
        
        {/* Dot Page Indicators - Show on both mobile and desktop */}
        {totalPages > 1 && (
          <div 
            className={styles.awardPagination}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: isMobile ? "20px" : "30px",
              padding: isMobile ? "0 10px" : "0",
            }}
          >
            {Array.from({ length: totalPages }).map((_, index) => (
              <motion.button
                key={index}
                style={{
                  backgroundColor: awardCurrentPage === index + 1 ? "#8bc34a" : "#ddd",
                  border: "none",
                  borderRadius: "50%",
                  width: isMobile ? "10px" : "12px",
                  height: isMobile ? "10px" : "12px",
                  margin: isMobile ? "0 6px" : "0 8px",
                  padding: 0,
                  cursor: "pointer",
                  fontSize: "0",
                  transition: "all 0.3s ease",
                }}
                whileHover={{ 
                  scale: 1.2,
                  backgroundColor: awardCurrentPage === index + 1 ? "#7cb342" : "#ccc"
                }}
                onClick={() => handleAwardPageChange(index + 1)}
                aria-label={`Go to award page ${index + 1}`}
                aria-current={awardCurrentPage === index + 1 ? "page" : undefined}
              />
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default AwardsSection